import { initializeApp, getApps } from "firebase/app";
import { getAuth, onAuthStateChanged, signInAnonymously } from "firebase/auth";
import {
  set as fbSet,
  update as fbUpdate,
  push as fbPush,
  get as fbGet,
  onValue as fbOnValue,
} from "firebase/database";

function resolveDatabaseUrl() {
  const fromEnv = process.env.REACT_APP_FIREBASE_DATABASE_URL;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).trim();
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
  if (projectId) return `https://${projectId}-default-rtdb.firebaseio.com`;
  return null;
}

/** Single Firebase app for Realtime Database (scribble lobby sync). */
export function getFirebaseApp() {
  const databaseURL = resolveDatabaseUrl();
  if (!databaseURL) return null;
  if (getApps().length > 0) return getApps()[0];
  return initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  });
}

export function isFirebaseConfigured() {
  return Boolean(resolveDatabaseUrl() && process.env.REACT_APP_FIREBASE_API_KEY);
}

let authReadyPromise = null;

/** Realtime Database rules typically require `auth != null` — sign in anonymously once, lazily. */
export function ensureFirebaseAuth() {
  const app = getFirebaseApp();
  if (!app) return Promise.resolve(null);
  if (!authReadyPromise) {
    const auth = getAuth(app);
    authReadyPromise = new Promise((resolve) => {
      if (auth.currentUser) {
        resolve(auth.currentUser);
        return;
      }
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          unsubscribe();
          resolve(user);
        }
      });
      signInAnonymously(auth).catch((err) => {
        console.warn("[firebase] anonymous sign-in failed:", err?.message || err);
        unsubscribe();
        resolve(null);
      });
    });
  }
  return authReadyPromise;
}

/** Auth-gated drop-in replacements for the raw firebase/database functions. */
export async function set(ref, value) {
  await ensureFirebaseAuth();
  return fbSet(ref, value);
}

export async function update(ref, value) {
  await ensureFirebaseAuth();
  return fbUpdate(ref, value);
}

export async function push(ref, value) {
  await ensureFirebaseAuth();
  return fbPush(ref, value);
}

export async function get(ref) {
  await ensureFirebaseAuth();
  return fbGet(ref);
}

export function onValue(ref, onNext, onError) {
  let unsubscribe = () => {};
  let cancelled = false;
  ensureFirebaseAuth()
    .then(() => {
      if (cancelled) return;
      unsubscribe = fbOnValue(ref, onNext, onError);
    })
    .catch((err) => {
      if (onError) onError(err);
    });
  return () => {
    cancelled = true;
    unsubscribe();
  };
}
